/**
 * client/input.ts
 * Responsabilidad: Captura y transformación de input del usuario sobre el canvas y controles táctiles.
 *
 * Soporta control por ratón en PC y control relativo continuo mediante Joystick Virtual táctil
 * para dispositivos móviles y tablets.
 */

import { Camera } from "./camera";
import { sendMove } from "./network";
import { MAP_WIDTH, MAP_HEIGHT } from "../src/shared/constants";
import type { Room } from "@colyseus/sdk";

export class InputController {
    private readonly canvas: HTMLCanvasElement;
    private readonly camera: Camera;
    private readonly room: Room;

    private lastSentTime: number = 0;
    private lastSentX: number = -Infinity;
    private lastSentY: number = -Infinity;
    private readonly minIntervalMs: number = 33; // ~30 paquetes/s
    private readonly minDistancePx: number = 5;

    /** Vector direccional activo del joystick táctil (-1 a 1) */
    private joystickDirX: number = 0;
    private joystickDirY: number = 0;
    private joystickActive: boolean = false;

    /** Posición de pantalla del puntero para recálculo continuo con cámara móvil */
    private pointerScreenX: number = -1;
    private pointerScreenY: number = -1;
    private hasPointer: boolean = false;

    private readonly onPointerMoveBound: (event: PointerEvent) => void;
    private readonly onPointerLeaveBound: () => void;

    constructor(canvas: HTMLCanvasElement, camera: Camera, room: Room) {
        this.canvas = canvas;
        this.camera = camera;
        this.room = room;

        this.onPointerMoveBound = this.handlePointerMove.bind(this);
        this.onPointerLeaveBound = () => {
            this.hasPointer = false;
        };

        this.canvas.addEventListener("pointermove", this.onPointerMoveBound);
        this.canvas.addEventListener("pointerleave", this.onPointerLeaveBound);
    }

    /** Procesa el movimiento del ratón (ignora eventos touch si el joystick está activo) */
    private handlePointerMove(event: PointerEvent): void {
        if (event.pointerType === "touch" && this.joystickActive) {
            return;
        }
        const rect = this.canvas.getBoundingClientRect();
        this.pointerScreenX = event.clientX - rect.left;
        this.pointerScreenY = event.clientY - rect.top;
        this.hasPointer = true;

        const worldTarget = this.camera.screenToWorld(this.pointerScreenX, this.pointerScreenY);
        this.maybeSendMove(worldTarget.x, worldTarget.y);
    }

    /** Actualiza continuamente el objetivo de movimiento del ratón respecto a la cámara móvil */
    public updateMouseTarget(): void {
        if (!this.hasPointer || this.joystickActive) return;
        const worldTarget = this.camera.screenToWorld(this.pointerScreenX, this.pointerScreenY);
        this.maybeSendMove(worldTarget.x, worldTarget.y);
    }

    /** Actualiza el vector del joystick táctil desde main.ts */
    public setJoystickVector(dirX: number, dirY: number, active: boolean): void {
        this.joystickDirX = dirX;
        this.joystickDirY = dirY;
        this.joystickActive = active;
    }

    /** Llamado en cada frame desde gameLoop para proyectar el movimiento del joystick relativo al jugador */
    public updateTouchTarget(playerWorldX: number, playerWorldY: number): void {
        if (!this.joystickActive) return;
        const mag = Math.hypot(this.joystickDirX, this.joystickDirY);
        if (mag < 0.08) return;

        const projectionDistance = 360;
        const targetX = Math.max(0, Math.min(MAP_WIDTH, playerWorldX + (this.joystickDirX / mag) * projectionDistance));
        const targetY = Math.max(0, Math.min(MAP_HEIGHT, playerWorldY + (this.joystickDirY / mag) * projectionDistance));
        this.maybeSendMove(targetX, targetY);
    }

    private maybeSendMove(targetX: number, targetY: number): void {
        const now = performance.now();
        const elapsed = now - this.lastSentTime;
        const distance = Math.hypot(targetX - this.lastSentX, targetY - this.lastSentY);

        if (elapsed >= this.minIntervalMs && distance >= this.minDistancePx) {
            try {
                sendMove(this.room, targetX, targetY);
                this.lastSentTime = now;
                this.lastSentX = targetX;
                this.lastSentY = targetY;
            } catch {
                // Silenciar errores transitorios de red
            }
        }
    }

    public destroy(): void {
        this.canvas.removeEventListener("pointermove", this.onPointerMoveBound);
        this.canvas.removeEventListener("pointerleave", this.onPointerLeaveBound);
    }
}
